// Single order getters
export function singleOrderItem(state) {
  return state.singleOrderItem;
}

export function isSingleOrderMode(state) {
  return state.singleOrderMode;
}

export function externalPoItems(state) {
  return Array.isArray(state.externalPoItems) ? state.externalPoItems : [];
}

export function hasExternalPoItems(state) {
  return Array.isArray(state.externalPoItems) && state.externalPoItems.length > 0;
}

// Helper: Get base cart items (regular cart or single order mode)
function itemsArray(state) {
  return Array.isArray(state.listings) ? state.listings : [];
}

// Helper: Get effective cart items (handles single order mode)
function getEffectiveCartItems(state) {
  if (state.singleOrderMode && state.singleOrderItem) {
    return [state.singleOrderItem];
  }
  return itemsArray(state);
}

// Helper: Filter available items
function availableItemsArray(state) {
  return itemsArray(state).filter((item) => item.is_available === true);
}

// Helper: Filter available items with single order mode support
function getEffectiveAvailableItems(state) {
  if (state.singleOrderMode && state.singleOrderItem) {
    return state.singleOrderItem.is_available === true
      ? [state.singleOrderItem]
      : [];
  }
  return availableItemsArray(state);
}

// Helper: Filter unavailable items
function unavailableItemsArray(state) {
  return itemsArray(state).filter((item) => item.is_available === false);
}

function lineFinalAmount(item) {
  const qty = Number(item.quantity ?? 0);
  if (!Number.isFinite(qty) || qty <= 0) return 0;

  if (Number.isFinite(item.total_price)) {
    return Number(item.total_price);
  }

  const isHalfCase = item.inventory_unit === "half-case";

  let unit = Number.isFinite(item.final_price)
    ? Number(item.final_price)
    : Number(item.unit_price ?? item.price ?? 0);

  if (!Number.isFinite(unit)) return 0;

  if (isHalfCase) {
    unit = Math.ceil((unit / 2) * 100) / 100;
  }

  return unit * qty;
}

function lineDiscountSaved(item) {
  const savedFromDiscounts = (item.applied_discounts || []).reduce(
    (s, d) => s + Number(d?.amount_saved_total ?? d?.amount_saved ?? 0),
    0
  );
  return savedFromDiscounts > 0 ? savedFromDiscounts : 0;
}

function lineOriginalAmount(item) {
  const qty = Number(item.quantity ?? 0);

  let itemOriginalPrice = item.original_price;
  if (item?.inventory_unit === "half-case") {
    itemOriginalPrice = Math.ceil(Number(item.original_price) / 2);
  }
  if (Number.isFinite(itemOriginalPrice)) {
    return Number(itemOriginalPrice) * qty;
  }
  return lineFinalAmount(item) + lineDiscountSaved(item);
}

// Modified cartItems to handle single order mode
export function cartItems(state) {
  return getEffectiveCartItems(state);
}

// Available items (for checkout calculations)
export function availableCartItems(state) {
  return getEffectiveAvailableItems(state);
}

// Unavailable items (to display with warnings)
export function unavailableCartItems(state) {
  if (state.singleOrderMode && state.singleOrderItem) {
    return state.singleOrderItem.is_available === false
      ? [state.singleOrderItem]
      : [];
  }
  return unavailableItemsArray(state);
}

// Check if cart has any unavailable items
export function hasUnavailableItems(state) {
  return getEffectiveCartItems(state).some(
    (item) => item.is_available === false
  );
}

// Check if ALL cart items are unavailable (cart has items, but none available)
export function allItemsUnavailable(state) {
  const items = getEffectiveCartItems(state);
  return items.length > 0 && items.every((item) => item.is_available === false);
}

export function totalCartQuantity(state) {
  return getEffectiveCartItems(state).reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  );
}

export function cartTotalPrice(state) {
  return getEffectiveAvailableItems(state).reduce(
    (sum, item) => sum + lineFinalAmount(item),
    0
  );
}

export function cartOriginalTotalPrice(state) {
  return getEffectiveAvailableItems(state).reduce(
    (sum, item) => sum + lineOriginalAmount(item),
    0
  );
}

export function cartTotalDiscountAmount(state) {
  return getEffectiveAvailableItems(state).reduce((sum, item) => {
    const diff = lineOriginalAmount(item) - lineFinalAmount(item);
    return sum + (diff > 0 ? diff : 0);
  }, 0);
}

export function getCartItemById(state) {
  return (cartItemId) => {
    if (
      state.singleOrderMode &&
      state.singleOrderItem &&
      state.singleOrderItem.id === cartItemId
    ) {
      return state.singleOrderItem;
    }
    return itemsArray(state).find((item) => item.id === cartItemId);
  };
}

export function isLoading(state) {
  return state.isLoading;
}

export function getError(state) {
  return state.lastError;
}
